import { ReactNode } from "react";
import { Section, stateColor } from "./primitives";

const Block = ({ title, testid, children }: { title: string; testid: string; children: ReactNode }) => (
  <div data-testid={testid}>
    <p className="nl-mono text-[9.5px] font-bold uppercase tracking-[0.25em] text-[#A78BFA]">{title}</p>
    <div className="mt-2 text-[13px] text-gray-400 leading-relaxed">{children}</div>
  </div>
);

const STATUS_DOT: Record<string, string> = {
  current: "#34D399", delayed: "#F7931A", unavailable: "#EF4444",
};

export const MethodologyPanel = ({ methodology, dataStatus }: { methodology: any; dataStatus: any }) => {
  const m = methodology || {};
  const div = m.divergence || {};
  const bands = m.state_bands || [];
  return (
    <Section num="09" title="Methodology & Transparency" sub={`v${m.version || "1.0"}`}
      testid="li-methodology-section">
      <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
        <Block title="Score range & states" testid="li-meth-states">
          <p>
            The index reads {m.score_range?.[0] ?? 0}–{m.score_range?.[1] ?? 100}, where {m.score_range?.[0] ?? 0} is
            extreme fear and {m.score_range?.[1] ?? 100} is extreme greed.
          </p>
          <div className="mt-3 flex h-1.5 rounded-full overflow-hidden">
            {bands.map((b: any) => (
              <div key={b.label} style={{
                width: `${b.max - b.min + 1}%`, background: stateColor(b.label), opacity: 0.75,
              }} />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {bands.map((b: any) => (
              <span key={b.label} className="nl-mono text-[9.5px]" style={{ color: stateColor(b.label) }}>
                {b.min}–{b.max} {b.label}
              </span>
            ))}
          </div>
        </Block>
        <Block title="Components & weights" testid="li-meth-weights">
          <div className="space-y-2.5">
            {(m.weights || []).map((w: any) => (
              <div key={w.key}>
                <div className="flex items-baseline justify-between">
                  <span className="text-gray-300">{w.name}</span>
                  <span className="nl-mono text-[11px] text-white font-bold">{w.weight}%</span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="h-full rounded-full bg-[#7C3AED]/70" style={{ width: `${w.weight}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Block>
        <Block title="Update cadence & freshness" testid="li-meth-cadence">
          <p>
            Recomputed approximately every {m.update_minutes ?? 30} minutes from the last 24 hours of
            NewsLayer coverage plus live BTC &amp; ETH prices. Any read older than {m.freshness_minutes ?? 45} minutes
            triggers an immediate recomputation, so the published score is never stale by design.
          </p>
        </Block>
        <Block title="Missing-data behavior" testid="li-meth-missing">
          <p>
            If the live market price feed is unreachable, Market Momentum holds neutral (50) for that
            computation and the input is flagged as delayed below. Historical values are never
            interpolated, backfilled or estimated — gaps stay visible.
          </p>
        </Block>
        <Block title="Divergence methodology" testid="li-meth-divergence">
          <p>
            The Fear &amp; Greed comparison is display-only and never blended into the index. A spread of
            ≥ {div.spread_threshold ?? 10} pts determines which signal leads; opposite
            {" "}{div.trend_window_days ?? 7}-day moves of ≥ {div.trend_threshold ?? 5} pts each flag
            DIVERGING; chart diamonds mark days where the two indices differed by
            ≥ {div.chart_mark_threshold ?? 15} pts.
          </p>
        </Block>
        <Block title="Event markers" testid="li-meth-events">
          <p>
            A day qualifies as an index event when the score moves ≥ {m.event_threshold ?? 5} pts
            day-over-day. The threshold lives in the index service configuration, not in this page.
          </p>
        </Block>
        <Block title="Sources & provenance" testid="li-meth-sources">
          <p>
            Inputs: the NewsLayer coverage corpus (headline sentiment and breaking-news activity) and
            live BTC &amp; ETH market prices. The Crypto Fear &amp; Greed Index shown for comparison is
            published by Alternative.me and is displayed with attribution — it is never an input.
          </p>
        </Block>
        <Block title="Versioning" testid="li-meth-version">
          <p>
            Layer Index Methodology <span className="text-white font-semibold">v{m.version || "1.0"}</span>,
            effective since {m.effective_date || "—"}. The methodology version is stored with every daily
            snapshot, so a future revision can never silently rewrite what historical scores meant.
          </p>
        </Block>
      </div>

      <div className="mt-12 rounded-xl border border-white/[0.08] bg-[#0b0c10] p-5" data-testid="li-data-status">
        <div className="flex flex-wrap items-center gap-3">
          <p className="nl-mono text-[10px] font-bold uppercase tracking-[0.25em] text-gray-400">Data status</p>
          <span className={`nl-mono text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded border ${
            (dataStatus?.delayed ?? 0) === 0
              ? "border-[#34D399]/40 text-[#34D399]"
              : "border-[#F7931A]/40 text-[#F7931A]"}`}
            data-testid="li-data-status-summary">
            {dataStatus?.summary || "—"}
          </span>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-5">
          {(dataStatus?.inputs || []).map((inp: any) => (
            <div key={inp.key} data-testid={`li-data-input-${inp.key}`}>
              <p className="flex items-center gap-2 text-[13px] text-white font-medium">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: STATUS_DOT[inp.status] || "#8b93ad" }} />
                {inp.name}
                <span className="nl-mono text-[9px] uppercase tracking-widest ml-auto"
                  style={{ color: STATUS_DOT[inp.status] || "#8b93ad" }}>
                  {inp.status}
                </span>
              </p>
              <p className="text-[11.5px] text-gray-500 leading-snug mt-1.5 pl-3.5">{inp.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
